import { SentryError } from "../sentry/SentryError";
import type { RemiPackage } from "./errorTypes/remi-package";
import { getRemiErrorDetails } from "./getRemiErrorDetails";
import type { RemiErrorInput } from "./remiErrorInput";

/**
 * External service error for handling errors from third-party integrations
 * @param RemiPackage the external service that caused the error
 * @param severity will determine incident response that Remi takes. Use with caution
 * @param errorType is a specific, service-specific error. These can be reused, but it should be descriptive enough to know exactly what happened based on the service and error type
 * @param data is an object tied to an errorType. This should be used to provide necessary context, like the id of the failed record, webhook url, etc. Must be serializable.
 */
export class RemiError extends SentryError<RemiPackage, RemiErrorInput["errorType"], RemiErrorInput["data"]> {
  constructor(input: RemiErrorInput) {
    const errorDetails = getRemiErrorDetails(input);
    const customFingerprintPrefix = input.fingerprintPrefix ? `${input.fingerprintPrefix} · ` : "";
    const name = `${customFingerprintPrefix}${input.remiPackage} · ${input.errorType}`;
    const message = errorDetails.sentryDescription + (input.additionalDescription ?? "");
    super({
      name,
      message,
      severity: input.severity,
      service: input.remiPackage,
      errorType: input.errorType,
      data: input.data,
      publicMessage: message,
      customFingerprintPrefix: input.fingerprintPrefix,
      metric: input.metric,
      cause: input.cause,
    });
  }

  getSentryData() {
    const tags: Record<string, string | boolean> = {
      external_service: this.service,
      error_type: this.errorType,
      severity: this.severity,
    };
    if (this.token) {
      tags.token = this.token;
    }
    if (this.origin) {
      tags.origin = this.origin;
    }
    return {
      tags,
      attributes: {
        data: this.reportedData,
      },
      fingerprint: [this.service, this.errorType, this.severity],
    };
  }
}
